import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface LensFlareParams {
  brightness: number;
  lensType: number;
  positionX: number;
  positionY: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const LensFlare: Filter<LensFlareParams> = {
  id: "lens-flare",
  displayName: "Lens Flare",
  defaultParams: { brightness: 100, lensType: 1, positionX: 50, positionY: 50 },

  apply(input: PixelBuffer, params: LensFlareParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const cx = (params.positionX / 100) * w;
    const cy = (params.positionY / 100) * h;
    const brightness = params.brightness / 100;
    const maxDist = Math.sqrt(w * w + h * h);

    const flareColors = [
      { r: 255, g: 200, b: 150, size: 0.05 },
      { r: 255, g: 255, b: 200, size: 0.03 },
      { r: 200, g: 220, b: 255, size: 0.08 },
      { r: 255, g: 180, b: 100, size: 0.02 },
    ];

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const dx = x - cx, dy = y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy) / maxDist;

        let flareR = 0, flareG = 0, flareB = 0;

        for (const flare of flareColors) {
          const flareDist = dist / flare.size;
          const intensity = Math.exp(-flareDist * flareDist) * brightness;
          flareR += flare.r * intensity;
          flareG += flare.g * intensity;
          flareB += flare.b * intensity;
        }

        const glow = Math.exp(-dist * 3) * brightness * 0.5;
        flareR += 255 * glow;
        flareG += 255 * glow;
        flareB += 255 * glow;

        dst[o] = clamp8(Math.min(255, src[o]! + flareR));
        dst[o + 1] = clamp8(Math.min(255, src[o + 1]! + flareG));
        dst[o + 2] = clamp8(Math.min(255, src[o + 2]! + flareB));
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};
